import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ShopContext = createContext();
const CART_STORAGE_KEY = "aifighter_cart_items";
const WISHLIST_STORAGE_KEY = "aifighter_wishlist_items";

function readStoredArray(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function ShopProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => readStoredArray(CART_STORAGE_KEY));
  const [wishlist, setWishlist] = useState(() => readStoredArray(WISHLIST_STORAGE_KEY));
  const [compareList, setCompareList] = useState([]);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = useCallback(async (itemType, itemId, quantity = 1, meta = {}) => {
    const qty = Math.max(1, Number(quantity) || 1);
    const key = `${itemType}:${itemId}`;
    setCartItems((items) => {
      const existing = items.find((item) => item.key === key);
      if (existing) {
        return items.map((item) => (
          item.key === key
            ? { ...item, quantity: item.quantity + qty, meta: { ...item.meta, ...meta } }
            : item
        ));
      }
      return [...items, { key, itemType, itemId, quantity: qty, meta, addedAt: new Date().toISOString() }];
    });
    return { ok: true };
  }, []);

  const updateCartQty = useCallback((key, quantity) => {
    const qty = Math.max(1, Number(quantity) || 1);
    setCartItems((items) => items.map((item) => item.key === key ? { ...item, quantity: qty } : item));
  }, []);

  const removeFromCart = useCallback((keyOrType, itemId) => {
    const key = itemId == null ? keyOrType : `${keyOrType}:${itemId}`;
    setCartItems((items) => items.filter((item) => item.key !== key));
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const toggleWishlist = useCallback(async (itemType, itemId, meta = {}) => {
    const key = `${itemType}:${itemId}`;
    let wished = false;
    setWishlist((items) => {
      if (items.some((item) => item.key === key)) {
        wished = false;
        return items.filter((item) => item.key !== key);
      }
      wished = true;
      return [...items, { key, itemType, itemId, meta, addedAt: new Date().toISOString() }];
    });
    return { ok: true, wished };
  }, []);

  const isWishlisted = useCallback((itemType, itemId) => (
    wishlist.some((item) => item.key === `${itemType}:${itemId}`)
  ), [wishlist]);

  const addToCompare = useCallback((item) => {
    setCompareList((items) => items.some((it) => it.key === item.key) ? items : [...items, item]);
  }, []);

  const removeFromCompare = useCallback((key) => {
    setCompareList((items) => items.filter((item) => item.key !== key));
  }, []);

  const isInCompare = useCallback((key) => compareList.some((item) => item.key === key), [compareList]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [cartItems]
  );

  const value = useMemo(() => ({
    cartItems,
    cartCount,
    addToCart,
    updateCartQty,
    removeFromCart,
    clearCart,
    wishlist,
    toggleWishlist,
    isWishlisted,
    compareList,
    addToCompare,
    removeFromCompare,
    isInCompare,
  }), [
    cartItems, cartCount, addToCart, updateCartQty, removeFromCart, clearCart,
    wishlist, toggleWishlist, isWishlisted, compareList, addToCompare,
    removeFromCompare, isInCompare,
  ]);

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop() {
  const ctx = useContext(ShopContext);
  if (!ctx) {
    return {
      cartItems: [],
      cartCount: 0,
      addToCart: () => {},
      removeFromCart: () => {},
      wishlist: [],
      toggleWishlist: () => false,
      isWishlisted: () => false,
      compareList: [],
      addToCompare: () => {},
      removeFromCompare: () => {},
      isInCompare: () => false,
    };
  }
  return ctx;
}

export default ShopContext;
